/**
 * Montagem do payload de atualização de contrato (UpdateContractRequestDto).
 * Centraliza a lógica de mapeamento do estado do formulário para o DTO da API,
 * eliminando duplicação entre update simples e edição de grupo.
 */

import { cleanPhone, normalizeCep, normalizeRepresentativePhoneInput, renegociacaoLocalDigitsFromFormField } from '../utils/stringUtils';
import { normalizeDeliveryAddressStreetNumberForApi } from '../../utils/deliveryAddressStreetNumber';
import { normalizeDeliveryAddressStreetNameForApi } from '../../utils/deliveryAddressStreetName';
import { normalizeContractSaleTokenForForm } from '../../utils/contractClusterTokens';
import { isPlanCompatibleWithNegociacao } from '../../utils/contractPlanCompatibility';
import type {
  UpdateContractRequestDto,
  UpdateContractDeliveryAddressDto,
  UpdateContractAdministratorDto,
  UpdateContractRepresentativeDto,
  UpdateContractAuthorizedReceiverDto,
  UpdateContractWitnessDto,
  UpdateContractLineDto,
  UpdateContractOfferDto,
} from './ContractService';
import type { ContractResponseDto } from './ContractService';
import { EContractStatus } from './ContractService';
import type {
  ContractLineFormItem,
  ContractRepresentanteFormItem,
  ContractRecebedorFormItem,
  ContractTestemunhaFormItem,
} from '../../types/contractForm';

const PERSISTED_ENTITY_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Só envia id de entidade já persistida no contrato (ignora manual-/partner-/temp-). */
export function asPersistedContractEntityId(id: string | undefined | null): string | undefined {
  if (!id) return undefined;
  const trimmed = id.trim();
  if (!PERSISTED_ENTITY_ID_RE.test(trimmed)) return undefined;
  return trimmed;
}

/** Entrada para montagem de PATCH de representantes (modal/formulário). */
export type RepresentativePayloadSource = {
  id?: string;
  nome?: string;
  cpf?: string;
  rg?: string;
  email?: string;
  telefone?: string;
};

/** Mapeia representantes para PATCH (ids só de entidades persistidas no contrato). */
export function mapRepresentativesToUpdateDto(
  representatives: RepresentativePayloadSource[]
): UpdateContractRepresentativeDto[] {
  return representatives.map(
    (r): UpdateContractRepresentativeDto => ({
      id: asPersistedContractEntityId(r.id),
      nome: (r.nome ?? '').trim(),
      cpf: (r.cpf || '').trim(),
      rg: r.rg?.trim() || undefined,
      email: (r.email ?? '').trim(),
      telefone: normalizeRepresentativePhoneInput(r.telefone ?? ''),
    })
  );
}

/** Em grupo, ids são por contrato — recria composição a partir dos dados do formulário. */
export function mapRepresentativesToUpdateDtoForGroupPatch(
  representatives: RepresentativePayloadSource[]
): UpdateContractRepresentativeDto[] {
  return mapRepresentativesToUpdateDto(representatives).map(({ id: _id, ...rest }) => rest);
}

/** Snapshot do formulário no momento do save (dados necessários para montar o payload) */
export interface ContractUpdateFormSnapshot {
  updatedAt?: string;
  tipoContratacao: string;
  diaVencimento: number;
  valorTotalMensal: number;
  numeroP2B: string;
  comprometimentoMinimo: string;
  fastchip: boolean;
  tipoFatura: string;
  envioFatura: string;
  origemVenda: string;
  /** '' = automático (API resolve); caso contrário token explícito (Senior ou operacional TIM). */
  token: string;
  deliveryAddress: {
    cep: string;
    cidade: string;
    estado: string;
    endereco: string;
    numero: string;
    bairro: string;
    complemento?: string;
    referencia?: string;
  };
  administrator: {
    nomeCompleto: string;
    cpf: string;
    rg: string;
    email: string;
    telefone1: string;
    telefone2: string;
  };
  representatives: ContractRepresentanteFormItem[];
  authorizedReceivers: ContractRecebedorFormItem[];
  witnesses: ContractTestemunhaFormItem[];
  lines: ContractLineFormItem[];
}

/**
 * Mapeia uma linha do formulário para o DTO de linha do update.
 * Regra Renegociação: remove DDD do número se já estiver no início.
 * Linhas com id começando em 'linha-' são consideradas novas (id = undefined).
 */
export function mapLineToUpdateDto(
  l: ContractLineFormItem,
  fastchip: boolean
): UpdateContractLineDto {
  let dddToSend = l.ddd;
  let numeroToSend = l.numero?.trim() || '';

  if (l.negociacao === 'Renegociação' && l.ddd && l.numero) {
    numeroToSend = renegociacaoLocalDigitsFromFormField(l.ddd, l.numero);
    dddToSend = l.ddd;
  }

  const isNewLine = typeof l.id === 'string' && l.id.startsWith('linha-');
  const lineId = isNewLine ? undefined : (l.id || undefined);

  return {
    id: lineId,
    negociacao: l.negociacao || undefined,
    ddd: dddToSend || undefined,
    numero: numeroToSend || undefined,
    tipoVenda: l.tipoVenda || undefined,
    planId: l.planId != null && String(l.planId).trim() !== '' ? String(l.planId).trim() : undefined,
    aparelho: l.aparelho?.trim() || undefined,
    handsetDeviceId: l.handsetDeviceId || undefined,
    aparelhoValorTotal: l.aparelhoValorTotal,
    aparelhoValorMensal: l.aparelhoValorMensal,
    valorChip: l.valorChip != null ? parseFloat(String(l.valorChip)) : undefined,
    valorMensal: Number(l.valorMensal ?? l.valorTotalMensal ?? 0),
    quantidade: l.quantidade,
    chipCcid: l.negociacao === 'Aditivo' && fastchip ? (l.chipCcid?.trim() || undefined) : undefined,
    excluirDoContrato: l.excluirDoContrato ?? false,
    requerSuspensao: l.requerSuspensao ?? false,
    trocaLinha: l.trocaLinha ?? false,
    meuTimLogin: l.meuTimLogin || undefined,
    meuTimSenha: l.meuTimSenha || undefined,
    protocolo: l.protocolo || undefined,
    dataDesativacao: l.marcarParaDesativar && l.dataDesativacao ? l.dataDesativacao : undefined,
    seraUsadaPeloCliente:
      l.seraUsadaPeloCliente !== undefined && l.seraUsadaPeloCliente !== null
        ? l.seraUsadaPeloCliente
        : null,
    ehPortabilidade: l.ehPortabilidade ?? false,
    portabilidadeNomeCompleto: l.ehPortabilidade ? (l.portabilidadeNomeCompleto || undefined) : undefined,
    portabilidadeCpf: l.ehPortabilidade ? (l.portabilidadeCpf || undefined) : undefined,
    portabilidadeOperadora: l.ehPortabilidade ? (l.portabilidadeOperadora || undefined) : undefined,
    portabilidadeLinha: l.ehPortabilidade ? (l.portabilidadeLinha || undefined) : undefined,
    portabilidadeCnpjCedente: l.ehPortabilidade ? (l.portabilidadeCnpjCedente || undefined) : undefined,
    portabilidadeRazaoSocialCedente: l.ehPortabilidade
      ? (l.portabilidadeRazaoSocialCedente || undefined)
      : undefined,
    portabilidadeRepresentantes:
      l.ehPortabilidade && l.portabilidadeRepresentantes && l.portabilidadeRepresentantes.length > 0
        ? l.portabilidadeRepresentantes.map((rep) => ({ nome: rep.nome, cpf: rep.cpf }))
        : undefined,
    isEsim: l.negociacao === 'Aditivo' && fastchip ? (l.isEsim ?? false) : undefined,
    ofertas: (l.ofertas || []).map(
      (o): UpdateContractOfferDto => ({
        nomeOferta: o.nomeOferta,
        valorMensal: o.valorMensal,
        incluso: o.incluso,
      })
    ),
  };
}

/** Linha que entra no valor mensal / contagem ativa (suspensas ficam no contrato, mas fora do faturamento). */
export function isBillableContractLine(line: ContractLineFormItem): boolean {
  return !line.excluirDoContrato;
}

export type ContractLineFieldValidationResult = {
  lineIndex: number;
  lineId?: string;
  message: string;
};

export type ContractPlanValidationOption = { id: string; name: string };

/** Limpa seleção de plano ao trocar o tipo de negociação (estado real do formulário). */
export function clearContractLinePlanSelection(): Pick<
  ContractLineFormItem,
  'planId' | 'plano' | 'planName' | 'valorMensal' | 'valorTotalMensal'
> {
  return {
    planId: '',
    plano: '',
    planName: '',
    valorMensal: 0,
    valorTotalMensal: undefined,
  };
}

/** Valida campos obrigatórios de linhas ativas (mesmas regras da criação / modal de adicionar linha). */
export function getFirstContractLineFieldValidationError(
  lines: ContractLineFormItem[],
  availablePlans?: ContractPlanValidationOption[]
): ContractLineFieldValidationResult | null {
  const linhasParaValidar = lines.filter(isBillableContractLine);

  for (const linha of linhasParaValidar) {
    const displayIndex = Math.max(1, lines.findIndex((l) => l.id === linha.id) + 1);

    if (!linha.planId || String(linha.planId).trim() === '') {
      return {
        lineIndex: displayIndex,
        lineId: linha.id,
        message: `Selecione um plano para a linha ${displayIndex}.`,
      };
    }

    if (availablePlans && availablePlans.length > 0) {
      const selectedPlan = availablePlans.find((plan) => plan.id === linha.planId);
      if (!selectedPlan) {
        return {
          lineIndex: displayIndex,
          lineId: linha.id,
          message: `Selecione um plano para a linha ${displayIndex}.`,
        };
      }

      if (!isPlanCompatibleWithNegociacao(selectedPlan.name, linha.negociacao)) {
        return {
          lineIndex: displayIndex,
          lineId: linha.id,
          message: `Selecione um plano compatível com ${linha.negociacao} na linha ${displayIndex}.`,
        };
      }
    }

    const rawValorMensal = linha.valorMensal ?? linha.valorTotalMensal;
    const valorMensalTexto =
      rawValorMensal == null ? '' : String(rawValorMensal).trim();
    if (!valorMensalTexto || Number.isNaN(Number(valorMensalTexto))) {
      return {
        lineIndex: displayIndex,
        lineId: linha.id,
        message: `Informe o valor mensal da linha ${displayIndex}.`,
      };
    }

    const quantidade = Number(linha.quantidade);
    if (!Number.isFinite(quantidade) || quantidade <= 0) {
      return {
        lineIndex: displayIndex,
        lineId: linha.id,
        message: `A quantidade da linha ${displayIndex} deve ser maior que zero.`,
      };
    }

    if (linha.negociacao === 'Aditivo' && !linha.ddd?.trim()) {
      return {
        lineIndex: displayIndex,
        lineId: linha.id,
        message: `DDD é obrigatório para a linha ${displayIndex} (Aditivo).`,
      };
    }

    if (linha.negociacao === 'Renegociação') {
      if (!linha.ddd?.trim()) {
        return {
          lineIndex: displayIndex,
          lineId: linha.id,
          message: `DDD é obrigatório para a linha ${displayIndex} (Renegociação).`,
        };
      }

      const numeroLocal = cleanPhone(linha.numero ?? '');
      if (numeroLocal.length < 8) {
        return {
          lineIndex: displayIndex,
          lineId: linha.id,
          message: `Número é obrigatório para a linha ${displayIndex} (Renegociação, mínimo 8 dígitos).`,
        };
      }
    }
  }

  return null;
}

/**
 * Monta o payload completo de update a partir do snapshot do formulário.
 * Usar em handleSaveContract para edição simples (um contrato).
 * Linhas suspensas (excluirDoContrato) devem ser enviadas com flags — igual na criação.
 */
export function buildContractUpdatePayload(snapshot: ContractUpdateFormSnapshot): UpdateContractRequestDto {
  const lines = snapshot.lines.map((l) => mapLineToUpdateDto(l, snapshot.fastchip));

  const tokenNormalized = normalizeContractSaleTokenForForm(snapshot.token, snapshot.fastchip);

  return {
    updatedAt: snapshot.updatedAt,
    tipoContratacao: snapshot.tipoContratacao,
    diaVencimento: snapshot.diaVencimento,
    valorTotalMensal: snapshot.valorTotalMensal,
    numeroP2B: snapshot.numeroP2B || undefined,
    comprometimentoMinimo: snapshot.comprometimentoMinimo
      ? parseFloat(snapshot.comprometimentoMinimo)
      : undefined,
    /** '' deixa a API resolver por região; valor válido para o modo força token explícito. */
    token: tokenNormalized,
    fastchip: snapshot.fastchip,
    tipoFatura: snapshot.tipoFatura || 'Mensal',
    envioFatura: snapshot.envioFatura || 'Email',
    origemVenda: snapshot.origemVenda || 'Site',
    deliveryAddress: {
      cep: normalizeCep(snapshot.deliveryAddress.cep),
      cidade: snapshot.deliveryAddress.cidade,
      estado: snapshot.deliveryAddress.estado,
      endereco: normalizeDeliveryAddressStreetNameForApi(snapshot.deliveryAddress.endereco),
      numero: normalizeDeliveryAddressStreetNumberForApi(snapshot.deliveryAddress.numero),
      bairro: snapshot.deliveryAddress.bairro,
      complemento: snapshot.deliveryAddress.complemento || undefined,
      referencia: snapshot.deliveryAddress.referencia || undefined,
    } as UpdateContractDeliveryAddressDto,
    administrator: {
      nomeCompleto: snapshot.administrator.nomeCompleto.trim(),
      cpf: snapshot.administrator.cpf.trim(),
      rg: (snapshot.administrator.rg || '000000000').trim(),
      email: snapshot.administrator.email?.trim() || undefined,
      telefone1: snapshot.administrator.telefone1?.trim() || undefined,
      telefone2: snapshot.administrator.telefone2?.trim() || undefined,
    } as UpdateContractAdministratorDto,
    representatives: mapRepresentativesToUpdateDto(snapshot.representatives),
    authorizedReceivers: snapshot.authorizedReceivers.map(
      (r): UpdateContractAuthorizedReceiverDto => {
        const isManual = 'manual' in r && r.manual;
        return {
          id: asPersistedContractEntityId(r.id),
          nome: r.nome,
          qualificacao: r.qualificacao?.trim() || undefined,
          cpf: (r.cpf || '').trim(),
          rg: r.rg?.trim() || undefined,
          email: isManual && 'email' in r ? (r as ContractRecebedorFormItem).email?.trim() || undefined : undefined,
          telefone:
            isManual && 'telefone' in r
              ? (r as ContractRecebedorFormItem).telefone?.trim() || undefined
              : undefined,
        };
      }
    ),
    witnesses: snapshot.witnesses.map(
      (t): UpdateContractWitnessDto => ({
        id: asPersistedContractEntityId(t.id),
        nome: t.nome,
        cpf: (t.cpf || '').trim(),
        rg: t.rg?.trim() || undefined,
      })
    ),
    lines,
  };
}

/**
 * Para edição de grupo: separa as linhas por sourceContractId e monta o payload
 * para um contrato específico do grupo. Linhas sem sourceContractId (novas) são
 * atribuídas ao contrato cujo tipo contém a negociação da linha.
 */
export function resolveGroupContractLines(
  lines: ContractLineFormItem[],
  groupContracts: ContractResponseDto[],
  contractId: string
): ContractLineFormItem[] {
  const contract = groupContracts.find((gc) => gc.id === contractId);
  if (!contract) return [];

  return lines.filter((l) => {
    if (!l.negociacao) return false;
    const owner = findActiveGroupContractForNegociacao(groupContracts, l.negociacao);
    return owner?.id === contractId && contractMatchesNegociacao(contract, l.negociacao);
  });
}

const CANCELLED_STATUSES = new Set<number>([
  EContractStatus.Cancelado,
  EContractStatus.CanceladoVtme,
]);

export function contractMatchesNegociacao(
  contract: { tipoContratacao?: string; contractType?: string | null },
  negociacao: string
): boolean {
  const tipo = contract.tipoContratacao || contract.contractType || '';
  if (negociacao === 'Aditivo') return tipo.includes('Aditivo');
  if (negociacao === 'Renegociação') return tipo.includes('Renegociação');
  return false;
}

export function isActiveGroupContractForRouting(contract: ContractResponseDto): boolean {
  const status = contract.status;
  if (status == null) return true;
  return !CANCELLED_STATUSES.has(status);
}

export function findActiveGroupContractForNegociacao(
  groupContracts: ContractResponseDto[],
  negociacao: string
): ContractResponseDto | undefined {
  return groupContracts.find(
    (gc) => isActiveGroupContractForRouting(gc) && contractMatchesNegociacao(gc, negociacao)
  );
}

export function getRequiredNegociacoesFromLines(lines: ContractLineFormItem[]): string[] {
  const set = new Set<string>();
  for (const line of lines) {
    if (line.excluirDoContrato) continue;
    if (line.negociacao === 'Aditivo' || line.negociacao === 'Renegociação') {
      set.add(line.negociacao);
    }
  }
  return Array.from(set);
}

export function groupNeedsEnsureForLines(
  lines: ContractLineFormItem[],
  groupContracts: ContractResponseDto[] | null | undefined,
  anchorTipoContratacao: string
): boolean {
  const required = getRequiredNegociacoesFromLines(lines);
  if (required.length === 0) return false;

  const members =
    groupContracts && groupContracts.length > 0 ? groupContracts : [];

  if (members.length === 0) {
    if (required.length > 1) return true;
    return !contractMatchesNegociacao({ tipoContratacao: anchorTipoContratacao }, required[0]!);
  }

  return required.some((n) => !findActiveGroupContractForNegociacao(members, n));
}

export function assignSourceContractIdsToLines(
  lines: ContractLineFormItem[],
  groupContracts: ContractResponseDto[]
): ContractLineFormItem[] {
  return lines.map((line) => {
    if (line.excluirDoContrato) return line;
    const target = findActiveGroupContractForNegociacao(groupContracts, line.negociacao);
    if (!target?.id) return line;
    return { ...line, sourceContractId: target.id };
  });
}

/** Prioridade do PATCH: receptores de linhas movidas → demais com linhas → vazios por último. */
function getGroupContractLinePatchPriority(
  contractId: string,
  lines: ContractLineFormItem[],
  groupContracts: ContractResponseDto[]
): number {
  const lineCount = resolveGroupContractLines(lines, groupContracts, contractId).length;
  if (lineCount === 0) return 2;

  const receivesMovedLine = lines.some((line) => {
    if (line.excluirDoContrato || !line.negociacao || !line.lineOriginContractId) return false;
    if (line.lineOriginContractId === contractId) return false;
    const owner = findActiveGroupContractForNegociacao(groupContracts, line.negociacao);
    return owner?.id === contractId;
  });
  if (receivesMovedLine) return 0;

  const emitsMovedLine = lines.some((line) => {
    if (line.excluirDoContrato || !line.negociacao || !line.lineOriginContractId) return false;
    if (line.lineOriginContractId !== contractId) return false;
    const owner = findActiveGroupContractForNegociacao(groupContracts, line.negociacao);
    return owner?.id != null && owner.id !== contractId;
  });
  if (emitsMovedLine) return 1;

  return 0;
}

/** Receptores primeiro; emissores depois; vazios por último (evita soft-delete antes da adoção). */
export function sortGroupContractsForLinePatch(
  groupContracts: ContractResponseDto[],
  lines: ContractLineFormItem[]
): ContractResponseDto[] {
  return [...groupContracts].sort(
    (a, b) =>
      getGroupContractLinePatchPriority(a.id, lines, groupContracts) -
      getGroupContractLinePatchPriority(b.id, lines, groupContracts)
  );
}

export function buildContractUpdatePayloadForGroup(
  snapshot: ContractUpdateFormSnapshot,
  groupContracts: ContractResponseDto[],
  contractId: string,
  updatedAtForContract?: string
): UpdateContractRequestDto {
  const contractLines = resolveGroupContractLines(snapshot.lines, groupContracts, contractId);
  const basePayload = buildContractUpdatePayload(snapshot);
  return {
    ...basePayload,
    updatedAt: updatedAtForContract ?? basePayload.updatedAt,
    lines: contractLines.map((l) => mapLineToUpdateDto(l, snapshot.fastchip)),
  };
}

/** Payload mínimo para edição somente de linhas (atalho na modal de detalhe). */
export function buildLinesOnlyPatchPayload(
  lines: ContractLineFormItem[],
  fastchip: boolean,
  updatedAt?: string,
  token?: string
): UpdateContractRequestDto {
  const tokenNormalized = token !== undefined ? normalizeContractSaleTokenForForm(token, fastchip) : '';
  return {
    updatedAt,
    fastchip,
    ...(tokenNormalized ? { token: tokenNormalized } : {}),
    lines: lines.map((l) => mapLineToUpdateDto(l, fastchip)),
  };
}

export function buildLinesOnlyPatchPayloadForGroup(
  lines: ContractLineFormItem[],
  fastchip: boolean,
  groupContracts: ContractResponseDto[],
  contractId: string,
  updatedAtForContract?: string,
  token?: string
): UpdateContractRequestDto {
  const contractLines = resolveGroupContractLines(lines, groupContracts, contractId);
  const tokenNormalized = token !== undefined ? normalizeContractSaleTokenForForm(token, fastchip) : '';
  return {
    updatedAt: updatedAtForContract,
    fastchip,
    ...(tokenNormalized ? { token: tokenNormalized } : {}),
    lines: contractLines.map((l) => mapLineToUpdateDto(l, fastchip)),
  };
}

export interface ContractEmptyOnSaveItem {
  contractId: string;
  contractType?: string;
  label: string;
}

/** Contratos que ficarão sem linhas após o save (para confirmação de cancelamento). */
export function getContractsThatWillBeEmpty(
  lines: ContractLineFormItem[],
  fastchip: boolean,
  editingContractId: string,
  editingGroupContracts?: ContractResponseDto[]
): ContractEmptyOnSaveItem[] {
  if (editingGroupContracts && editingGroupContracts.length > 1) {
    return editingGroupContracts
      .filter((gc) => {
        const contractLines = resolveGroupContractLines(lines, editingGroupContracts, gc.id);
        return contractLines.length === 0;
      })
      .map((gc) => ({
        contractId: gc.id,
        contractType: gc.contractType || gc.tipoContratacao,
        label: gc.contractType || gc.tipoContratacao || gc.id,
      }));
  }

  const activeLines = lines.filter(isBillableContractLine);
  if (activeLines.length > 0) {
    return [];
  }

  const single = editingGroupContracts?.[0];
  return [
    {
      contractId: editingContractId,
      contractType: single?.contractType || single?.tipoContratacao,
      label: single?.contractType || single?.tipoContratacao || editingContractId,
    },
  ];
}
